import {
    getResourceAggregations,
    isResourceRecommended,
    isResourceUsable,
} from 'app/overview/dashboard/config/resource_selection';
import { isAccumulated } from 'app/overview/dashboard/config/utils';
import I from 'immutable';
import { isNil } from 'lodash';
import {
    DashboardItemService,
    DataSourceResource,
    ImmutableVisConfig,
} from '../dashboard_item.service';
import { ConfigService } from 'app/config/config.service';

/**
 * Sentinel value here essentially means I want 1 group irrelevant of the actual size of the group (time slice in seconds).
 * The actual equation is `sliceSizeInSeconds === timeFrameInSeconds` but we just use this sentinel value
 * because the api expects a groupSize of 0. Using the timeFrameInSeconds as the timeSliceSize should also work, but we're doing this instead.
 * @type {number}
 */
export const SINGLE_GROUP_TIME_SLICE_IN_SECONDS: number = 0;

/**
 * When using accumulated charts, i.e. SINGLE_GROUP_TIME_SLICE_IN_SECONDS is used, we don't want to use the
 * accumulated stat as soon as possible. This is because the result can be skewed. The worst case is a
 * 2 hour time frame with a 1 hour aggregated stat. Only 1 actual stat will be included in the aggregation which means
 * we have an hour of unaccounted time with 'null' data, i.e. 50% of unaccounted time.
 *
 * Providing this constant so that moving to the next stage of aggregation occurs at the multiplier * the
 * aggregation period.
 *
 * For example, a value of 5 will ensure at least 4 hourly samples will be available in a 5 hour time frame.
 * i.e. 20% of unaccounted time. Season to taste.
 */
export const TIME_FRAME_TO_TIME_SLICE_SIZE_MULTIPLIER_FOR_ACCUMULATED_CHARTS: number = 5;

const TIME_SLICE_SIZES = I.List.of(
    1,
    15,
    30,
    60,
    180,
    300,
    900,
    1800,
    3600,
    10800,
    21600,
    43200,
    86400,
    604800,
    1209600,
    2419200,
    SINGLE_GROUP_TIME_SLICE_IN_SECONDS,
);

const TIME_SLICE_SIZE_NAMES = I.Map([
    [1, '1 Second'],
    [15, '15 Seconds'],
    [30, '30 Seconds'],
    [60, '1 Minute'],
    [180, '3 Minutes'],
    [300, '5 Minutes'],
    [900, '15 Minutes'],
    [1800, '30 Minutes'],
    [3600, '1 Hour'],
    [10800, '3 Hours'],
    [21600, '6 Hours'],
    [43200, '12 Hours'],
    [86400, '24 Hours'],
    [604800, '1 Week'],
    [1209600, '2 Weeks'],
    [2419200, '4 Weeks'],
    [SINGLE_GROUP_TIME_SLICE_IN_SECONDS, 'All'],
]);

// When time slice size is set to 'auto', try to get as close as possible to this many total slices
const AUTO_TARGET_NUMBER_OF_SLICES = 100;

export function validTimeSliceSizes(
    dataSource: string,
    timeFrameInSeconds: number,
    configService: ConfigService,
): I.List<number> {
    const minDataSourceTimeSliceSize = DashboardItemService.dataSourceDetails.getIn([
        dataSource,
        'minTimeSliceSize',
    ]);
    const resources = getResourceAggregations(dataSource);
    const maxNumberOfSlices = configService.maxNumberOfSlices;
    const singleTimeSliceOptionEnabled = configService.showTimeSliceOptionSingleSlice;

    return TIME_SLICE_SIZES.filter((sliceSizeInSeconds) => {
        if (
            sliceSizeInSeconds === SINGLE_GROUP_TIME_SLICE_IN_SECONDS &&
            singleTimeSliceOptionEnabled
        ) {
            return true;
        }
        // total slices > 1
        const reqA = sliceSizeInSeconds < 0.5 * timeFrameInSeconds;
        // total slices < max
        const reqB = sliceSizeInSeconds > timeFrameInSeconds / maxNumberOfSlices;
        // each slice > data source minimum
        const reqC =
            sliceSizeInSeconds >= minDataSourceTimeSliceSize || !minDataSourceTimeSliceSize;
        // must have at least a usable resource
        const reqD = !!resources.find((agg) =>
            isResourceUsable(agg, sliceSizeInSeconds, timeFrameInSeconds),
        );

        return reqA && reqB && reqC && reqD;
    }).toList();
}

function getNumberOfSlicesGivenSliceSize(sliceSize: number, timeFrameInSeconds: number): number {
    return sliceSize === SINGLE_GROUP_TIME_SLICE_IN_SECONDS
        ? 1
        : Math.floor(timeFrameInSeconds / sliceSize);
}

function isThereAPartialTimeSlice(sliceSize: number, timeFrameInSecond: number): boolean {
    return !(timeFrameInSecond % sliceSize == 0);
}

function timeSliceNameStr(sliceSize: number, timeFrameInSeconds: number) {
    if (!isNil(sliceSize) && !isNil(timeFrameInSeconds)) {
        const numSlices = getNumberOfSlicesGivenSliceSize(sliceSize, timeFrameInSeconds);
        return `${TIME_SLICE_SIZE_NAMES.get(sliceSize)} (${numSlices} slice${
            numSlices === 1 ? '' : 's'
        })`;
    } else {
        return '';
    }
}

/**
 * Picks an appropriate time slice size in seconds to use.
 * There's a few parameters it will watch out for:
 *
 * - try and get a certain amount of slices
 * - make sure there's a suitable aggregation table for this time slice size.
 *    For example if you aim for 100 slices, on a 12 hour time frame that would be 432 seconds per slice.
 *    We make sure there's a suitable rollup table that can give us at least 1 stat every 432 seconds and not be destroyed by the huge time frame.
 * - if no time slice with a suitable aggregation is found, we still want to return one to avoid issues. So this function will be called recursively
 *    with includeRecommendedCheck=false, this is will ignore the resource check and just use the best time slice for the time frame. It is still possible
 *    for this to return null if there's no time slice that can be used. This can happen if the time frame is too small and showTimeSliceOptionSingleSlice
 *    is set to false.
 */
function chooseTimeSlice(
    sliceSizeSeconds: Array<number>,
    resources: Array<DataSourceResource>,
    idealNumberOfSlices: number,
    timeFrameInSeconds: number,
    includeRecommendedCheck: boolean = true,
): number {
    let bestSize = null;
    let bestOffsetFromIdeaNumberOfSlices = null;
    for (const size of sliceSizeSeconds) {
        const numberOfSlices = timeFrameInSeconds / size;
        const offsetFromIdeaNumberOfSlices = numberOfSlices - idealNumberOfSlices;
        const isAbsolutelyBetter =
            Math.abs(offsetFromIdeaNumberOfSlices) < Math.abs(bestOffsetFromIdeaNumberOfSlices);
        const isEqualButMore =
            Math.abs(offsetFromIdeaNumberOfSlices) === Math.abs(bestOffsetFromIdeaNumberOfSlices) &&
            offsetFromIdeaNumberOfSlices > bestOffsetFromIdeaNumberOfSlices;

        // make sure we can actually use this time slice size with a resource
        const hasSuitableAggregate = includeRecommendedCheck
            ? !!resources.find((res) => isResourceRecommended(res, size, timeFrameInSeconds))
            : true;
        if (hasSuitableAggregate && (!bestSize || isAbsolutelyBetter || isEqualButMore)) {
            bestSize = size;
            bestOffsetFromIdeaNumberOfSlices = offsetFromIdeaNumberOfSlices;
        }
    }

    if (!bestSize && includeRecommendedCheck) {
        return chooseTimeSlice(
            sliceSizeSeconds,
            resources,
            idealNumberOfSlices,
            timeFrameInSeconds,
            false,
        );
    }

    return bestSize;
}

export function customTimeSliceSizeOptionsForConfig(
    config,
    timeFrameInSeconds: number,
    configService: ConfigService,
) {
    return validTimeSliceSizes(config.get('dataSource'), timeFrameInSeconds, configService).map(
        (value) =>
            I.Map({
                value,
                label: timeSliceNameStr(value, timeFrameInSeconds),
                partialTimeSliceExist: isThereAPartialTimeSlice(value, timeFrameInSeconds),
            }),
    );
}

export function autoTimeSliceSize(
    dataSource: string,
    timeFrameInSeconds: number,
    configService: ConfigService,
): number {
    const validTimeSlices = validTimeSliceSizes(dataSource, timeFrameInSeconds, configService);
    const availableResourceAggregations = getResourceAggregations(dataSource);
    return chooseTimeSlice(
        validTimeSlices.toJS(),
        availableResourceAggregations,
        AUTO_TARGET_NUMBER_OF_SLICES,
        timeFrameInSeconds,
    );
}

export function getTimeSliceSize(
    config: ImmutableVisConfig,
    timeFrameInSeconds: number,
    configService: ConfigService,
): number {
    if (isAccumulated(config)) {
        return 0;
    } else if (config.get('useAutoTimeSliceSize')) {
        return autoTimeSliceSize(config.get('dataSource'), timeFrameInSeconds, configService);
    } else {
        return config.get('customTimeSliceSize');
    }
}
